import { useI18n } from '@goparrot-dashboard/i18n';
import { useSquareNav } from '@goparrot-dashboard/shared-utils';
import classNames from 'classnames';
import React from 'react';
import { generatePath, match as RRMatch } from 'react-router';
import { NavLink } from 'react-router-dom';
import { Location as HLocation } from 'history';
import { LocationItem, MerchantItem } from '../../interface';
import { FRANCHISE_MANAGEMENT, MULTI_BRAND_MANAGEMENT } from '../constants';
import { toSentenceCase } from '../utils';

type SidebarItemProps = {
  item: MerchantItem | LocationItem;
  onClick(navLinkName: string): void;
  storeId: string;
  isTopLevel?: boolean;
};

export const SidebarItem: React.FC<SidebarItemProps> = ({ item, isTopLevel, onClick, storeId }) => {
  const { getIntlString } = useI18n();
  const isSquareNavEnabled = useSquareNav();
  const isFranchiseOrMultiBrand = [FRANCHISE_MANAGEMENT, MULTI_BRAND_MANAGEMENT].includes(item.label);
  const handleNavLinkClick = () => onClick(item.label);

  const label = getIntlString(item.label, { isRootPath: true });
  const capitalizedLabel = isSquareNavEnabled ? toSentenceCase(label) : label;
  const squareStyles = `${isTopLevel ? 'tw-pl-2' : 'tw-pl-10'} tw-pr-2`;

  const isSquarePushNotificationItem = isSquareNavEnabled && '/mobile-app/push-notifications' === item.link;

  /* workaround to show "Push notification" item active when user navigates to "customers" and "audiences" that are
   in different tabs under separate URLS */
  const getIsActive = (match: RRMatch | null, location: HLocation) => {
    const lowercasePathname = location.pathname.toLowerCase();

    return (
      lowercasePathname.includes('/mobile-app/push-notifications') ||
      lowercasePathname.includes('/mobile-app/customers') ||
      lowercasePathname.includes('/mobile-app/audiences')
    );
  };

  return (
    <NavLink
      data-testid={`${getIntlString(item.label, { isRootPath: true }).replace(/ /g, '-').toLowerCase()}-sidebar-item`}
      key={item.label}
      exact={item.exact}
      activeClassName="tw-text-blue-500 tw-bg-blue-100"
      isActive={isSquarePushNotificationItem ? getIsActive : undefined}
      className={classNames([
        'tw-flex tw-items-start tw-py-3 tw-whitespace-normal',
        'tw-text-black tw-leading-4 lg:hover:tw-text-blue-500 lg:hover:tw-opacity-100',
        'tw-transform paragraph-20 tw-rounded-lg',
        isSquareNavEnabled ? squareStyles : 'tw-px-1.5',
      ])}
      to={generatePath(item.link, { id: storeId })}
      onClick={handleNavLinkClick}
    >
      {item.icon && <span className={'tw-flex tw-mr-2 tw-pt-0.5 tw-w-4 tw-h-4'}>{item.icon}</span>}
      <span
        className={classNames([
          isFranchiseOrMultiBrand && 'tw-max-w-min',
          'tw-inline-block semibold-30  lg:hover:tw-text-blue-500 lg:hover:tw-opacity-100',
          isSquareNavEnabled ? 'tw-leading-6' : 'tw-leading-5',
        ])}
      >
        {capitalizedLabel}
      </span>
    </NavLink>
  );
};
